import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardBody, Button } from "reactstrap";
import { useHistory } from "react-router-dom";
import { Catalogue } from "../../../types/catalogue.types";
import notifications from "../../../common/Components/Notifications/Notification";
import {
  computeShareToVisitConversion,
  mapCatalogueProductToQuickRow,
  type QuickProductRow,
} from "./basicDetailsMetrics";
import { isSystemManagedCatalogue } from "../catalogueSystemManaged";

interface BasicDetailsTabProps {
  catalogue: Catalogue;
  onBack: () => void;
  /** Jump to Products tab and open the real add-products flow (API-backed). */
  onRequestAddProducts?: () => void;
  onExportData?: () => void;
  exporting?: boolean;
  onPreview?: () => void;
  onShare?: () => void;
}

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  "Content-Type": "application/json",
});

interface BasicSnapshot {
  totalProducts: number;
  quickProducts: QuickProductRow[];
  uniqueVisitors: number;
  totalViewEvents: number;
}

const BasicDetailsTab: React.FC<BasicDetailsTabProps> = ({
  catalogue,
  onBack,
  onRequestAddProducts,
  onExportData,
  exporting,
  onPreview,
  onShare,
}) => {
  const history = useHistory();

  const [formData, setFormData] = useState({
    name: catalogue.name || "",
    description: catalogue.description || "",
    category: catalogue.category || "",
    targetMarket: catalogue.targetMarket || "",
  });
  const [loading, setLoading] = useState(false);
  const [snapshotLoading, setSnapshotLoading] = useState(true);
  const [snapshot, setSnapshot] = useState<BasicSnapshot | null>(null);

  const sharesIssued = useMemo(() => {
    if (typeof catalogue.shared === "number") return catalogue.shared;
    if (Array.isArray(catalogue.sharedLinks)) return catalogue.sharedLinks.length;
    return 0;
  }, [catalogue.shared, catalogue.sharedLinks]);

  const loadSnapshot = useCallback(async () => {
    if (!catalogue?.id) return;
    setSnapshotLoading(true);
    try {
      const base = (process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");
      const [productsRes, statsRes] = await Promise.all([
        fetch(
          `${base}/catalouge/${encodeURIComponent(catalogue.id)}/products?offset=0&limit=20&sortBy=name&sortOrder=asc`,
          { headers: authHeaders() }
        ),
        fetch(`${base}/catalogue-analytics/${encodeURIComponent(catalogue.id)}/stats`, {
          headers: authHeaders(),
        }),
      ]);

      const productsJson = await productsRes.json().catch(() => ({}));
      const statsJson = await statsRes.json().catch(() => ({}));

      let totalProducts = 0;
      let quickProducts: QuickProductRow[] = [];

      if (productsJson?.success && productsJson?.data) {
        const d = productsJson.data;
        totalProducts =
          typeof d.catalogue?.totalProducts === "number"
            ? d.catalogue.totalProducts
            : typeof d.pagination?.total === "number"
              ? d.pagination.total
              : Array.isArray(d.products)
                ? d.products.length
                : 0;
        const raw = Array.isArray(d.products) ? d.products : [];
        quickProducts = raw.map(mapCatalogueProductToQuickRow);
      }

      let uniqueVisitors = 0;
      let totalViewEvents = 0;
      if (statsJson?.success && statsJson?.data) {
        uniqueVisitors = Number(
          statsJson.data.uniqueBuyers ?? statsJson.data.uniqueUsers ?? 0
        );
        totalViewEvents = Number(
          statsJson.data.buyerOpens ?? statsJson.data.totalViews ?? 0
        );
      }

      setSnapshot({
        totalProducts,
        quickProducts,
        uniqueVisitors,
        totalViewEvents,
      });
    } catch {
      setSnapshot({
        totalProducts: 0,
        quickProducts: [],
        uniqueVisitors: 0,
        totalViewEvents: 0,
      });
    } finally {
      setSnapshotLoading(false);
    }
  }, [catalogue.id]);

  useEffect(() => {
    void loadSnapshot();
  }, [loadSnapshot]);

  const conversion = useMemo(() => {
    const uv = snapshot?.uniqueVisitors ?? 0;
    return computeShareToVisitConversion(uv, sharesIssued);
  }, [snapshot?.uniqueVisitors, sharesIssued]);

  const categoryOptions = [
    "Festive Collection",
    "Casual Wear",
    "Formal Wear",
    "Export Collection",
    "Textiles",
    "Handicrafts",
    "Fashion",
  ];

  const targetMarketOptions = [
    "Domestic",
    "Export - EU",
    "Export - US",
    "Export - Middle East",
    "B2B International",
    "B2B Domestic",
    "B2C Online",
  ];

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdateCatalogue = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${process.env.REACT_APP_SERVERURL}/catalouge/update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          catalougeId: catalogue.id,
          name: formData.name.trim(),
          description: formData.description.trim(),
          category: formData.category.trim(),
          targetMarket: formData.targetMarket.trim(),
        }),
      });

      if (response.ok) {
        notifications({
          title: "Success!",
          message: "Catalogue updated successfully",
          type: "success",
        });
        onBack();
      } else {
        notifications({
          title: "Error!",
          message: "Failed to update catalogue",
          type: "danger",
        });
      }
    } catch {
      notifications({
        title: "Error!",
        message: "Failed to update catalogue",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badgeClass =
      status === "in_stock" ? "bg-success" : status === "low_stock" ? "bg-warning" : "bg-danger";
    return (
      <span className={`badge ${badgeClass} text-white`} style={{ fontSize: "10px" }}>
        {status.replace("_", " ").toUpperCase()}
      </span>
    );
  };

  const displayTotalProducts =
    snapshot && !snapshotLoading ? snapshot.totalProducts : catalogue.products ?? 0;
  const displayAccessed =
    snapshot && !snapshotLoading ? snapshot.uniqueVisitors : catalogue.accessed ?? 0;

  return (
    <div>
      {(onExportData || onPreview || onShare) && (
        <div className="catalogue-basic-actions">
          {onExportData ? (
            <Button
              outline
              type="button"
              className="catalogue-toolbar-btn catalogue-toolbar-btn-outline"
              disabled={exporting}
              onClick={onExportData}
            >
              {exporting ? "Exporting…" : "Export Data"}
            </Button>
          ) : null}
          {onPreview ? (
            <Button
              outline
              type="button"
              className="catalogue-toolbar-btn catalogue-toolbar-btn-outline"
              onClick={onPreview}
            >
              Preview
            </Button>
          ) : null}
          {onShare ? (
            <Button
              type="button"
              className="catalogue-toolbar-btn catalogue-toolbar-btn-solid"
              onClick={onShare}
            >
              Share
            </Button>
          ) : null}
        </div>
      )}
    <div className="catalogue-basic-page">
      <Card className="catalogue-panel catalogue-basic-summary">
        <CardHeader className="catalogue-panel-header">
          <h6 className="catalogue-panel-title">Catalogue Information</h6>
        </CardHeader>
        <CardBody className="catalogue-panel-body">
          <div className="catalogue-summary-grid">
            <div className="catalogue-summary-stat">
              <span>Created</span>
              <strong>{catalogue.created}</strong>
            </div>
            <div className="catalogue-summary-stat">
              <span>Products</span>
              <strong>{snapshotLoading ? "…" : displayTotalProducts}</strong>
            </div>
            <div className="catalogue-summary-stat">
              <span>Share links</span>
              <strong>{sharesIssued}</strong>
            </div>
            <div className="catalogue-summary-stat">
              <span>Visitors</span>
              <strong>{snapshotLoading ? "…" : displayAccessed}</strong>
            </div>
          </div>
          <div className="catalogue-summary-conversion">
            <div className="d-flex justify-content-between align-items-center">
              <span>Conversion</span>
              <strong>
                {snapshotLoading ? "…" : conversion.percent != null ? `${conversion.percent.toFixed(1)}%` : "—"}
              </strong>
            </div>
            <p>{conversion.label}</p>
            <div className="progress">
              <div
                className="progress-bar"
                style={{
                  width: `${conversion.percent != null ? Math.min(conversion.percent, 100) : 0}%`,
                }}
              />
            </div>
          </div>
          <div className="catalogue-field">
            <label htmlFor="catalogue-name">Catalogue name</label>
            <input
              id="catalogue-name"
              type="text"
              className="form-control"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
            />
          </div>
          <div className="catalogue-field">
            <label htmlFor="catalogue-description">Description</label>
            <textarea
              id="catalogue-description"
              className="form-control"
              rows={2}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
            />
          </div>
          <div className="catalogue-field-row">
            <div className="catalogue-field">
              <label htmlFor="catalogue-category">Category</label>
              <select
                id="catalogue-category"
                className="form-control"
                value={formData.category}
                onChange={(e) => handleInputChange("category", e.target.value)}
              >
                <option value="">Select category</option>
                {categoryOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="catalogue-field">
              <label htmlFor="catalogue-market">Target market</label>
              <select
                id="catalogue-market"
                className="form-control"
                value={formData.targetMarket}
                onChange={(e) => handleInputChange("targetMarket", e.target.value)}
              >
                <option value="">Select target market</option>
                {targetMarketOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="catalogue-panel-footer">
            <Button
              className="catalogue-toolbar-btn catalogue-toolbar-btn-solid"
              onClick={handleUpdateCatalogue}
              disabled={loading}
            >
              {loading ? "Updating…" : "Update Catalogue"}
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card className="catalogue-panel catalogue-basic-products">
        <CardHeader className="catalogue-panel-header">
          <h6 className="catalogue-panel-title">Quick Products</h6>
        </CardHeader>
        <CardBody className="catalogue-panel-body">
          <div className="catalogue-quick-products-scroll">
            {snapshotLoading ? (
              <div className="text-muted small px-1 py-2">Loading products…</div>
            ) : snapshot && snapshot.quickProducts.length === 0 ? (
              <div className="text-muted small px-1 py-2">No products in this catalogue yet.</div>
            ) : (
              snapshot?.quickProducts.map((product) => (
                <div
                  key={product.id || product.name}
                  className="catalogue-quick-product-row"
                >
                  <div className="catalogue-quick-product-thumb">
                    {product.imageUrl ? (
                      <img src={product.imageUrl} alt={product.name} />
                    ) : (
                      <span>No image</span>
                    )}
                  </div>
                  <div className="catalogue-quick-product-meta">
                    <div className="fw-medium small text-truncate">{product.name}</div>
                    <div className="mt-1">{getStatusBadge(product.status)}</div>
                  </div>
                  <div className="catalogue-quick-product-actions">
                    <Button
                      size="sm"
                      outline
                      disabled={!product.id}
                      className="catalogue-row-mini-btn"
                      onClick={() => product.id && history.push(`/product/${encodeURIComponent(product.id)}`)}
                    >
                      View
                    </Button>
                    <Button
                      size="sm"
                      outline
                      disabled={!product.id}
                      className="catalogue-row-mini-btn catalogue-row-mini-btn-gold"
                      onClick={() => product.id && history.push(`/edit-product/${encodeURIComponent(product.id)}`)}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
          {isSystemManagedCatalogue(catalogue) ? (
            <p className="text-muted small mb-0 mt-2">
              Products are added and moved automatically from inventory stock.
            </p>
          ) : (
          <Button
            type="button"
            className="catalogue-toolbar-btn catalogue-toolbar-btn-solid w-100"
            onClick={() => {
              if (onRequestAddProducts) {
                onRequestAddProducts();
                return;
              }
              notifications({
                title: "Add products",
                message: "Open the Products tab and use Add from inventory.",
                type: "info",
              });
            }}
          >
            Add from inventory
          </Button>
          )}
        </CardBody>
      </Card>

      <Card className="catalogue-panel catalogue-basic-notes">
        <CardHeader className="catalogue-panel-header">
          <h6 className="catalogue-panel-title">Catalogue Notes</h6>
        </CardHeader>
        <CardBody className="catalogue-panel-body">
          <div className="catalogue-field catalogue-notes-field">
            <label htmlFor="catalogue-notes">Internal notes</label>
            <textarea
              id="catalogue-notes"
              className="form-control catalogue-notes-input"
              placeholder="Buyer notes, deadlines, price holds, special instructions"
            />
          </div>
        </CardBody>
      </Card>
    </div>
    </div>
  );
};

export default BasicDetailsTab;
